import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAdminClient } from '../context/AdminClientContext';
import { useDashboardStats, useSimulations, usePopulations } from '../hooks/useClientScopedData';
import { useAnimatedCounter } from '../hooks/useAnimatedCounter';
import {
  Users,
  Zap,
  Play,
  Upload,
  FileText,
  BarChart3,
  ArrowRight,
  AlertCircle,
  Clock,
  Star,
  CheckCircle2,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import LatestResultCard from '../components/reporting/LatestResultCard';
import EmptyState from '../components/reporting/EmptyState';
import ReliabilitySummary from '../components/reporting/ReliabilitySummary';
import TokenBudgetBanner from '../components/dashboard/TokenBudgetBanner';

// Helper function to format date for Last Simulation
const formatLastSimDate = (dateString) => {
  if (!dateString) return 'Never';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Loading skeleton for stats card
const StatCardSkeleton = () => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 animate-pulse">
    <div className="flex items-center justify-between">
      <div className="space-y-2">
        <div className="h-4 w-20 bg-slate-200 rounded" />
        <div className="h-8 w-12 bg-slate-200 rounded" />
      </div>
      <div className="w-11 h-11 bg-slate-100 rounded-xl" />
    </div>
  </div>
);

// Loading skeleton for simulation item
const SimulationItemSkeleton = () => (
  <div className="flex items-center justify-between p-3 animate-pulse">
    <div className="flex items-center gap-3 flex-1">
      <div className="w-2 h-2 rounded-full bg-slate-200" />
      <div className="space-y-1.5 flex-1">
        <div className="h-4 w-3/4 bg-slate-200 rounded" />
        <div className="h-3 w-1/2 bg-slate-100 rounded" />
      </div>
    </div>
    <div className="h-6 w-20 bg-slate-100 rounded-full" />
  </div>
);

// Animated stat number component
const AnimatedStat = ({ value }) => {
  const animated = useAnimatedCounter(value || 0, 900);
  return <>{animated}</>;
};

// Status summary card — derives status counts and surfaces "needs attention"
const StatusCard = ({ label, value, icon: Icon, tone = 'slate', highlight = false, spin = false, testId }) => {
  const toneCls = {
    slate: 'bg-slate-50 dark:bg-slate-800/50 text-slate-500 dark:text-slate-400',
    emerald: 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-500 dark:text-emerald-400',
    blue: 'bg-blue-50 dark:bg-blue-500/10 text-blue-500 dark:text-blue-400',
    amber: 'bg-amber-50 dark:bg-amber-500/10 text-amber-500 dark:text-amber-400',
  }[tone];

  return (
    <div
      className={cn(
        'bg-white dark:bg-card rounded-xl border p-5 transition-all duration-200',
        highlight ? 'border-amber-200 dark:border-amber-500/40 shadow-[0_0_0_3px_rgba(251,191,36,0.08)]' : 'border-slate-200 dark:border-border hover:shadow-md hover:border-slate-300 dark:hover:border-primary/20'
      )}
      data-testid={testId}
    >
      <p className="text-xs font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wide">{label}</p>
      <div className="flex items-end justify-between mt-1">
        <p className="text-2xl font-semibold text-slate-900 dark:text-white tabular-nums">
          <AnimatedStat value={value} />
        </p>
        <div className={cn('w-7 h-7 rounded-md flex items-center justify-center', toneCls)}>
          <Icon className={cn('w-3.5 h-3.5', spin && 'animate-spin')} />
        </div>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const { user } = useAuth();
  const { activeClient } = useAdminClient();
  const { data: stats, loading: statsLoading } = useDashboardStats();
  const { data: allSimulations, loading: simsLoading } = useSimulations();
  const { data: populations } = usePopulations();
  
  // Get recently viewed from localStorage
  const [recentlyViewed, setRecentlyViewed] = useState([]);
  
  useEffect(() => {
    const viewed = JSON.parse(localStorage.getItem('synpop_recently_viewed') || '[]');
    setRecentlyViewed(viewed);
  }, []);
  
  // Map recently viewed IDs to simulation objects
  const recentlyViewedSims = recentlyViewed
    .map(id => allSimulations?.find(s => s.id === id))
    .filter(Boolean)
    .slice(0, 5);
  
  const recentSimulations = allSimulations ? allSimulations.slice(0, 5) : [];
  const starredSimulations = allSimulations?.filter(s => s.starred) || [];
  const loading = statsLoading;

  // Get the most recent completed simulation date
  const lastCompletedSim = allSimulations?.find(s => s.status === 'completed');
  const lastSimTime = lastCompletedSim?.completed_at || lastCompletedSim?.created_at;

  // Status summary derived from full simulations list — answers "what needs my attention?"
  const statusCounts = useMemo(() => {
    const list = allSimulations || [];
    const completed = list.filter((s) => s.status === 'completed').length;
    const running = list.filter((s) => s.status === 'running' || s.status === 'pending').length;
    // Needs Attention: failed runs OR completed runs with incomplete census or low-variation warning
    const needsAttention = list.filter(
      (s) =>
        s.status === 'failed' ||
        s.status === 'error' ||
        (s.status === 'completed' && (s.incomplete_census_flag || s.collapse_flag))
    ).length;
    return {
      total: list.length,
      completed,
      running,
      needsAttention,
    };
  }, [allSimulations]);
  
  // Helper to get population name for a simulation
  const getPopulationName = (sim) => {
    if (!populations || !sim.population_id) return null;
    const pop = populations.find(p => p.id === sim.population_id);
    return pop?.name || null;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-100 text-emerald-700';
      case 'running': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getStatusDot = (status) => {
    switch (status) {
      case 'completed': return 'bg-emerald-500';
      case 'running': return 'bg-blue-500 animate-pulse';
      default: return 'bg-gray-400';
    }
  };

  if (loading) {
    return (
      <div data-testid="dashboard-page" className="space-y-8 page-transition">
        {/* Header skeleton */}
        <div className="animate-fade-in">
          <div className="h-8 w-32 bg-slate-200 rounded animate-pulse" />
          <div className="h-4 w-64 bg-slate-100 rounded mt-2 animate-pulse" />
        </div>
        
        {/* Stats Cards Skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
          {[1, 2, 3, 4].map(i => <StatCardSkeleton key={i} />)}
        </div>
        
        {/* Two Column Layout Skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="h-5 w-28 bg-slate-200 rounded mb-4 animate-pulse" />
            <div className="space-y-2.5">
              {[1, 2, 3].map(i => (
                <div key={i} className="h-14 bg-slate-50 rounded-lg animate-pulse" />
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="h-5 w-36 bg-slate-200 rounded mb-4 animate-pulse" />
            <div className="space-y-2">
              {[1, 2, 3, 4, 5].map(i => <SimulationItemSkeleton key={i} />)}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="dashboard-page" className="space-y-10 page-transition">
      {/* Token Budget Banner — only renders at warning/exceeded states */}
      <TokenBudgetBanner isAdmin={user?.is_admin} activeClientId={activeClient?.id} />

      {/* Admin Client View Banner */}
      {user?.is_admin && activeClient && (
        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg animate-slide-up">
          <AlertCircle className="w-5 h-5 text-blue-600" />
          <span className="text-sm font-medium text-blue-900">
            Viewing as: <span className="font-bold">{activeClient.name}</span>
          </span>
        </div>
      )}
      
      {/* Header */}
      <div className="animate-fade-in">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Overview</h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">What needs your attention today?</p>
      </div>

      {/* Status Summary — answers "what needs my attention?" first */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-5" data-testid="status-summary-row">
        <StatusCard
          testId="status-total-simulations"
          label="Total Simulations"
          value={statusCounts.total}
          icon={Play}
          tone="slate"
        />
        <StatusCard
          testId="status-completed-simulations"
          label="Completed"
          value={statusCounts.completed}
          icon={CheckCircle2}
          tone="emerald"
        />
        <StatusCard
          testId="status-running-simulations"
          label="Running"
          value={statusCounts.running}
          icon={Loader2}
          tone="blue"
          spin={statusCounts.running > 0}
        />
        <StatusCard
          testId="status-needs-attention"
          label="Needs Attention"
          value={statusCounts.needsAttention}
          icon={AlertTriangle}
          tone={statusCounts.needsAttention > 0 ? 'amber' : 'slate'}
          highlight={statusCounts.needsAttention > 0}
        />
      </div>

      {/* Resources strip — populations & stimuli counts kept accessible */}
      <div className="flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400" data-testid="resources-strip">
        <Link to="/populations" className="inline-flex items-center gap-1.5 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
          <Users className="w-3.5 h-3.5" />
          <span><AnimatedStat value={stats?.populations || 0} /> populations</span>
        </Link>
        <span className="text-slate-300">·</span>
        <Link to="/stimuli" className="inline-flex items-center gap-1.5 hover:text-slate-800 dark:hover:text-slate-200 transition-colors">
          <Zap className="w-3.5 h-3.5" />
          <span><AnimatedStat value={stats?.stimuli || 0} /> stimuli</span>
        </Link>
        {lastSimTime && (
          <>
            <span className="text-slate-300">·</span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" />
              <span>Last run {formatLastSimDate(lastSimTime)}</span>
            </span>
          </>
        )}
      </div>

      {/* Latest Result — recommended next step */}
      {lastCompletedSim && (
        <LatestResultCard
          simulation={lastCompletedSim}
          populationName={getPopulationName(lastCompletedSim)}
          testId="dashboard-latest-result"
        />
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Quick Actions */}
        <div className="bg-white dark:bg-card rounded-xl border border-slate-200 dark:border-border p-5 card-entrance stagger-5 card-glow">
          <h2 className="text-sm font-semibold text-slate-900 dark:text-white mb-4">Quick Actions</h2>
          <div className="space-y-3">
            {/* #1 Primary Action - h-12 (48px), #10 Arrow microinteraction */}
            <Link to="/simulations" data-testid="quick-action-run-simulation" className="block group">
              <Button 
                className="w-full h-12 px-5 text-sm font-medium bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500 text-white shadow-sm mb-2 transition-all duration-150"
              >
                <Play className="w-4 h-4 mr-2 transition-transform duration-150 group-hover:translate-x-0.5" />
                Run Simulation
                <ArrowRight className="w-4 h-4 ml-2 opacity-0 -translate-x-2 transition-all duration-150 group-hover:opacity-100 group-hover:translate-x-0" />
              </Button>
            </Link>
            
            {/* #4 Secondary Actions - better hover feedback */}
            <Link
              to="/populations"
              data-testid="quick-action-upload-population"
              className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-border hover:bg-slate-50 dark:hover:bg-white/[0.03] hover:border-slate-300 dark:hover:border-primary/20 transition-all duration-[120ms] group cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-100 dark:bg-muted rounded-lg flex items-center justify-center group-hover:bg-blue-50 dark:group-hover:bg-blue-500/10 transition-all duration-[120ms]">
                  <Upload className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-[120ms]" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors duration-[120ms]">Upload Population</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:group-hover:text-slate-400 group-hover:translate-x-1 transition-all duration-[120ms]" />
            </Link>

            <Link
              to="/stimuli"
              data-testid="quick-action-create-stimulus"
              className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-border hover:bg-slate-50 dark:hover:bg-white/[0.03] hover:border-slate-300 dark:hover:border-primary/20 transition-all duration-[120ms] group cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-100 dark:bg-muted rounded-lg flex items-center justify-center group-hover:bg-blue-50 dark:group-hover:bg-blue-500/10 transition-all duration-[120ms]">
                  <FileText className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-[120ms]" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors duration-[120ms]">Create Stimulus</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:group-hover:text-slate-400 group-hover:translate-x-1 transition-all duration-[120ms]" />
            </Link>

            <Link
              to="/compare"
              data-testid="quick-action-compare-results"
              className="flex items-center justify-between p-3 rounded-lg border border-slate-200 dark:border-border hover:bg-slate-50 dark:hover:bg-white/[0.03] hover:border-slate-300 dark:hover:border-primary/20 transition-all duration-[120ms] group cursor-pointer"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-100 dark:bg-muted rounded-lg flex items-center justify-center group-hover:bg-blue-50 dark:group-hover:bg-blue-500/10 transition-all duration-[120ms]">
                  <BarChart3 className="w-4 h-4 text-slate-500 dark:text-slate-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-[120ms]" />
                </div>
                <span className="text-sm font-medium text-slate-600 dark:text-slate-300 group-hover:text-slate-900 dark:group-hover:text-white transition-colors duration-[120ms]">Compare Results</span>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:group-hover:text-slate-400 group-hover:translate-x-1 transition-all duration-[120ms]" />
            </Link>
          </div>
        </div>

        {/* #5 Recent Simulations - better scanability with dividers */}
        <div className="bg-white dark:bg-card rounded-xl border border-slate-200 dark:border-border p-5 card-entrance stagger-6 card-glow">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Clock className="w-4 h-4 text-slate-400 dark:text-slate-500" />
              Recent Simulations
            </h2>
            <Link to="/simulations" className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium transition-colors">
              View all →
            </Link>
          </div>
          <div className="divide-y divide-gray-200/40 dark:divide-slate-700/50">
            {simsLoading ? (
              [1, 2, 3, 4, 5].map(i => <SimulationItemSkeleton key={i} />)
            ) : recentSimulations.length === 0 ? (
              <EmptyState
                icon={Play}
                title="No completed simulations yet"
                description="Run a simulation to generate response distributions, segment breakdowns, and reliability diagnostics."
                primaryCta={{ label: 'Run Simulation', to: '/simulations' }}
                secondaryCta={{ label: 'Create Stimulus', to: '/stimuli' }}
                testId="dashboard-empty-recent-sims"
                className="py-8"
              />
            ) : (
              recentSimulations.map((sim, index) => {
                const isCompleted = sim.status === 'completed';
                const moduleHint = sim.config?.reporting_mode || sim.reporting_mode;
                return (
                  <Link
                    key={sim.id}
                    to={`/analysis?simulation=${sim.id}`}
                    data-testid={`recent-sim-${sim.id}`}
                    className={cn(
                      'flex items-center justify-between py-3.5 px-2 -mx-2 rounded-md hover:bg-gray-50 dark:hover:bg-slate-700/50 transition-all duration-[120ms] group cursor-pointer',
                      index === 0 && 'pt-0'
                    )}
                    onClick={() => {
                      const viewed = JSON.parse(localStorage.getItem('synpop_recently_viewed') || '[]');
                      const updated = [sim.id, ...viewed.filter(id => id !== sim.id)].slice(0, 10);
                      localStorage.setItem('synpop_recently_viewed', JSON.stringify(updated));
                    }}
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={cn('w-2 h-2 rounded-full flex-shrink-0 transition-transform duration-[120ms] group-hover:scale-125', getStatusDot(sim.status))} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white truncate transition-colors duration-[120ms]">{sim.name}</p>
                          {sim.starred && <Star className="w-3 h-3 text-amber-500 fill-amber-500 flex-shrink-0" />}
                          {moduleHint && (
                            <Badge
                              variant="outline"
                              className="text-[9px] uppercase tracking-wider px-1.5 py-0 bg-slate-50 text-slate-600 border-slate-200"
                              data-testid={`recent-sim-module-${sim.id}`}
                            >
                              {moduleHint.replace(/_/g, ' ')}
                            </Badge>
                          )}
                          {(sim.incomplete_census_flag || sim.collapse_flag) && (
                            <Badge
                              variant="outline"
                              className="text-[9px] px-1.5 py-0 bg-amber-50 text-amber-700 border-amber-200"
                              title={sim.incomplete_census_flag ? 'Incomplete census' : 'Low variation warning'}
                              data-testid={`recent-sim-warning-${sim.id}`}
                            >
                              <AlertTriangle className="w-2.5 h-2.5 mr-0.5" />
                              {sim.incomplete_census_flag ? 'Incomplete' : 'Low variation'}
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">
                          {sim.status === 'completed' ? 'Completed' : sim.status} · {new Date(sim.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          {getPopulationName(sim) && (
                            <span className="ml-1">· {getPopulationName(sim)}</span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isCompleted && (
                        <ReliabilitySummary
                          mode="compact"
                          simulation={sim}
                          testId={`recent-sim-reliability-${sim.id}`}
                        />
                      )}
                      <ArrowRight className="w-4 h-4 text-slate-300 dark:text-slate-600 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-[120ms]" />
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* Starred Simulations - if any */}
      {starredSimulations.length > 0 && (
        <div className="bg-white dark:bg-card rounded-xl border border-slate-200 dark:border-border p-5 card-entrance">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-sm font-semibold text-slate-900 dark:text-white flex items-center gap-2">
              <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
              Starred
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {starredSimulations.slice(0, 6).map(sim => (
              <Link
                key={sim.id}
                to={`/analysis?simulation=${sim.id}`}
                data-testid={`starred-sim-${sim.id}`}
                className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 dark:bg-muted/50 border border-slate-100 dark:border-border hover:border-slate-300 dark:hover:border-primary/20 hover:bg-gray-50 dark:hover:bg-white/[0.03] transition-all duration-[120ms] group cursor-pointer"
              >
                <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-700 dark:text-slate-200 group-hover:text-slate-900 dark:group-hover:text-white truncate transition-colors duration-[120ms]">{sim.name}</p>
                  <p className="text-xs text-slate-400 dark:text-slate-500">{sim.status === 'completed' ? 'Completed' : sim.status}</p>
                </div>
                <ArrowRight className="w-4 h-4 text-slate-300 dark:text-slate-600 group-hover:text-slate-500 dark:group-hover:text-slate-400 group-hover:translate-x-0.5 transition-all duration-[120ms]" />
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
